import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

export default function AdminTier2() {
  const { setPage } = useContext(AppContext);
  const [companies, setCompanies] = useState([]);
  const { t } = useI18n();
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const res = await api.get('/admin/tier2/companies');
      setCompanies(res.data.companies);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка');
    } finally {
      setLoading(false);
    }
  }

  async function selectCompany(comp) {
    setSelected(comp);
    try {
      const res = await api.get(`/admin/tier2/companies/${comp.company_id}`);
      setDetail(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка');
    }
  }

  async function enterCompany(companyId) {
    try {
      const { data } = await api.post(`/admin/tier2/companies/${companyId}/enter`);
      // API client uses sessionStorage jwt
      sessionStorage.setItem('jwt', data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      alert(`Вы вошли в "${data.company.name}" как суперадмин.`);
      window.location.reload();
    } catch (e) {
      setError(e.response?.data?.detail || 'Ошибка');
    }
  }

  if (loading) return <div className="page"><div className="spinner" /></div>;

  return (
    <div className="page">
      <button className="btn btn-link back-btn" onClick={() => setPage('dashboard')}>← Назад</button>
      <h1>🏗️ Компании Tier 2 ({companies.length})</h1>
      <p style={{ fontSize: 13, opacity: 0.6, marginBottom: 16 }}>
        Компании клиентов, которые ведут собственную бухгалтерию
      </p>

      {error && <div className="banner banner-warning">⚠️ {error}</div>}

      {companies.length === 0 ? (
        <div className="empty">Пока нет компаний Tier 2. Они появятся когда клиенты зарегистрируют свои фирмы.</div>
      ) : (
        <div className="list">
          {companies.map(c => (
            <div key={c.company_id} className="list-item" onClick={() => selectCompany(c)} style={{ cursor: 'pointer' }}>
              <div className="list-main">
                <div className="list-title">{c.company_name}</div>
                <div className="list-sub">
                  NIP: {c.nip} | Владелец: {c.owner.name}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(99,102,241,0.15)' }}>
                    👥 {c.stats.team_members} чел
                  </span>
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(16,185,129,0.15)' }}>
                    👷 {c.stats.employees} сотр
                  </span>
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(245,158,11,0.15)' }}>
                    📄 {c.stats.contracts} дог
                  </span>
                  {c.stats.pending_declarations > 0 && (
                    <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: 'rgba(239,68,68,0.2)', color: '#f87171' }}>
                      ⚠️ {c.stats.pending_declarations} декл
                    </span>
                  )}
                  <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: c.is_vat_payer ? 'rgba(16,185,129,0.15)' : 'rgba(107,114,128,0.15)' }}>
                    {c.is_vat_payer ? '✅ VAT' : '❌ без VAT'}
                  </span>
                </div>
              </div>
              <div className={`status-badge status-${c.status}`}>{c.status}</div>
            </div>
          ))}
        </div>
      )}

      {/* Company Detail */}
      {detail && (
        <div className="card" style={{ marginTop: 20, padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ margin: 0 }}>🏢 {detail.company.name}</h2>
            <button className="btn btn-primary btn-small" onClick={() => enterCompany(detail.company.id)}>
              🔑 Войти в компанию
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, marginBottom: 16 }}>
            <div>NIP: <b>{detail.company.nip}</b></div>
            <div>KRS: <b>{detail.company.krs || '—'}</b></div>
            <div>REGON: <b>{detail.company.regon || '—'}</b></div>
            <div>VAT: <b>{detail.company.is_vat_payer ? '✅ Czynny' : '❌ Nie'}</b></div>
            <div style={{ gridColumn: '1 / -1' }}>Адрес: {detail.company.address || '—'}</div>
          </div>

          {/* Owner */}
          {detail.owner && (
            <div style={{ marginBottom: 16 }}>
              <h3>👤 Владелец</h3>
              <div style={{ fontSize: 13 }}>
                {detail.owner.name} | {detail.owner.email || '—'} | TG: {detail.owner.telegram_id || '—'}
              </div>
            </div>
          )}

          {/* Team */}
          <h3>👥 Команда ({detail.team.length})</h3>
          {detail.team.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {detail.team.map(m => (
                <div key={m.id} style={{ fontSize: 13, padding: 6, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  {m.name} — <b>{m.role}</b> ({m.status})
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 13, opacity: 0.5, marginBottom: 16 }}>Только владелец</div>}

          {/* Employees */}
          <h3>👷 Сотрудники ({detail.employees.length})</h3>
          {detail.employees.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {detail.employees.map(e => (
                <div key={e.id} style={{ fontSize: 13, padding: 6, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  {e.name} | {e.status} | Найм: {e.hire_date || '—'} | ZUS: {e.zus_registered ? '✅' : '❌'}
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 13, opacity: 0.5, marginBottom: 16 }}>Нет сотрудников</div>}

          {/* Contracts */}
          <h3>📄 Договора ({detail.contracts.length})</h3>
          {detail.contracts.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              {detail.contracts.map(c => (
                <div key={c.id} style={{ fontSize: 13, padding: 6, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  {c.employee} — {c.type} | {c.gross} PLN | {c.start} → {c.end || '∞'} | ZUS: {c.zus ? '✅' : '❌'} | {c.status}
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 13, opacity: 0.5, marginBottom: 16 }}>Нет договоров</div>}

          {/* Declarations */}
          <h3>🏛️ Декларации ({detail.declarations.length})</h3>
          {detail.declarations.length > 0 ? (
            <div>
              {detail.declarations.map(d => (
                <div key={d.id} style={{
                  fontSize: 13, padding: 6, borderBottom: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex', justifyContent: 'space-between',
                }}>
                  <span>{d.type} | {d.period}</span>
                  <span>{d.amount.toLocaleString()} PLN</span>
                  <span className={`status-badge status-${d.status}`} style={{ fontSize: 11 }}>{d.status}</span>
                </div>
              ))}
            </div>
          ) : <div style={{ fontSize: 13, opacity: 0.5 }}>Нет деклараций</div>}
        </div>
      )}
    </div>
  );
}
